import { html } from '@effectivastudio/lit-components';
import BaseMenu from '@effectivastudio/lit-components/components/base_menu/element.js';
import '@effectivastudio/lit-components/components/dot_menu/element.js';
import translations from './translations.js';

/**
 * The + (plus) menu in Group streams
 *
 * @prop groupId - ID of the group
 * @prop menuTitle - Menu title on mobile
 * @prop cardEventOn - Event card creation action
 * @prop cardNewsOn  - News card creation action
 * @prop cardDiscussionOn - Discussion card creation action
 * @prop cardEngagementOn - Engagement card creation action
 * @prop cardPollOn - Poll card creation action
 * @prop cardDateOn - Date card creation action
 * @prop cardOrganizeOn - Organize card creation action
 * @prop groupPostsOn - Action for publishing Event card in group stream
 * @prop forumPostsOn - Action for publishing Event card in forum stream
 * @prop subgroupOn - Subgroup creation action
 * @prop sectionOn - Section creation action
 * @prop cardEventUrl - URL for Event card creation action
 * @prop cardNewsUrl - URL for News card creation action
 * @prop cardDiscussionUrl - URL for Discussion card creation action
 * @prop cardEngagementUrl - URL for Engagement card creation action
 * @prop cardPollUrl - URL for Poll card creation action
 * @prop cardDateUrl - URL for Date card creation action
 * @prop cardOrganizeUrl - URL for Organize card creation action
 * @prop groupPostsUrl - URL for group posts action
 * @prop forumPostsUrl - URL for forum posts action
 * @prop subgroupUrl - URL for subgroup creation action
 * @prop sectionUrl - URL for section creation action
 */

export default class GroupStreamMenu extends BaseMenu {
  static properties = {
    groupId: { type: Number },
    menuTitle: { type: String },

    // Permissions
    cardEventOn: { type: Boolean },
    cardNewsOn: { type: Boolean },
    cardDiscussionOn: { type: Boolean },
    cardEngagementOn: { type: Boolean },
    cardPollOn: { type: Boolean },
    cardDateOn: { type: Boolean },
    cardOrganizeOn: { type: Boolean },
    groupPostsOn: { type: Boolean },
    forumPostsOn: { type: Boolean },
    subgroupOn: { type: Boolean },
    sectionOn: { type: Boolean },

    // URLs
    cardEventUrl: { type: String },
    cardNewsUrl: { type: String },
    cardDiscussionUrl: { type: String },
    cardEngagementUrl: { type: String },
    cardPollUrl: { type: String },
    cardDateUrl: { type: String },
    cardOrganizeUrl: { type: String },
    groupPostsUrl: { type: String },
    forumPostsUrl: { type: String },
    subgroupUrl: { type: String },
    sectionUrl: { type: String },
  };

  /** @internal */
  static translations = translations;

  /** @internal */
  get instanceId() {
    return this.groupId;
  }

  /** @internal */
  get cardEventItem() {
    return {
      title: this.t('actions.event'),
      action: this.actionFor('groupStreamExecJSFromResponse', [
        this.cardEventUrl,
      ]),
      icon: 'calendar-day',
      partName: 'event',
    };
  }

  /** @internal */
  get cardNewsItem() {
    return {
      title: this.t('actions.news'),
      action: this.actionFor('groupStreamExecJSFromResponse', [
        this.cardNewsUrl,
      ]),
      icon: 'newspaper',
      partName: 'news',
    };
  }

  /** @internal */
  get cardDiscussionItem() {
    return {
      title: this.t('actions.discussion'),
      action: this.actionFor('groupStreamExecJSFromResponse', [
        this.cardDiscussionUrl,
      ]),
      icon: 'comments',
      partName: 'discussion',
    };
  }

  /** @internal */
  get cardEngagementItem() {
    return {
      title: this.t('actions.engagement'),
      action: this.actionFor('groupStreamExecJSFromResponse', [
        this.cardEngagementUrl,
      ]),
      icon: 'hand-heart',
      partName: 'engagement',
    };
  }

  /** @internal */
  get cardPollItem() {
    return {
      title: this.t('actions.poll'),
      action: this.actionFor('groupStreamExecJSFromResponse', [
        this.cardPollUrl,
      ]),
      icon: 'chart-pie',
      partName: 'poll',
    };
  }

  /** @internal */
  get cardDateItem() {
    return {
      title: this.t('actions.date'),
      action: this.actionFor('groupStreamExecJSFromResponse', [
        this.cardDateUrl,
      ]),
      icon: 'calendar-alt',
      partName: 'date-finder',
    };
  }

  /** @internal */
  get cardOrganizeItem() {
    return {
      title: this.t('actions.organize'),
      action: this.actionFor('groupStreamExecJSFromResponse', [
        this.cardOrganizeUrl,
      ]),
      icon: 'clipboard-list',
      partName: 'organize',
    };
  }

  /** @internal */
  get groupPostsItem() {
    return {
      title: this.t('actions.group_post'),
      action: this.actionFor('groupStreamExecJSFromResponse', [
        this.groupPostsUrl,
      ]),
      icon: 'newspaper',
      partName: 'group-stream',
    };
  }

  /** @internal */
  get forumPostsItem() {
    return {
      title: this.t('actions.forum_posts'),
      action: this.actionFor('groupStreamExecJSFromResponse', [
        this.forumPostsUrl,
      ]),
      icon: 'comments',
      partName: 'forum-stream',
    };
  }

  /** @internal */
  get subgroupItem() {
    return {
      title: this.t('actions.subgroup'),
      action: this.actionFor('groupStreamExecJSFromResponse', [
        this.subgroupUrl,
      ]),
      icon: 'users-between-lines',
      partName: 'subgroup',
    };
  }

  /** @internal */
  get sectionItem() {
    return {
      title: this.t('actions.section'),
      action: this.actionFor('groupStreamExecJSFromResponse', [
        this.sectionUrl,
      ]),
      icon: 'folder',
      partName: 'section',
    };
  }

  /** @internal */
  get items() {
    return [
      this.cardEventOn && this.cardEventItem,
      this.cardNewsOn && this.cardNewsItem,
      this.cardDiscussionOn && this.cardDiscussionItem,
      this.cardEngagementOn && this.cardEngagementItem,
      this.cardPollOn && this.cardPollItem,
      this.cardDateOn && this.cardDateItem,
      this.cardOrganizeOn && this.cardOrganizeItem,
      this.groupPostsOn && this.groupPostsItem,
      this.forumPostsOn && this.forumPostsItem,
      this.subgroupOn && this.subgroupItem,
      this.sectionOn && this.sectionItem,
    ].filter(Boolean);
  }

  /** @internal */
  get dotMenuConfig() {
    return {
      buttons: this.items,
      header: this.menuTitle,
      showCancel: true,
    };
  }

  actionFor(method, args) {
    return {
      menu: 'xs-group-stream-menu',
      id: this.instanceId,
      method: method,
      ...(args && { args: args }),
    };
  }

  groupStreamExecJSFromResponse(url) {
    this.commonActions.execJSFromResponse(url);
  }

  render() {
    return html`
      <ef-dot-menu .config=${this.dotMenuConfig}>
        <button
          slot="trigger"
          class="xs-btn btn-primary xs-btn-rounded xs-btn-lg">
          <ef-icon name="plus:solid"></ef-icon>
        </button>
      </ef-dot-menu>
    `;
  }
}

GroupStreamMenu.registerTagName('xs-group-stream-menu');
GroupStreamMenu.registerCommonActions('xs-group-stream-menu');

customElements.get('xs-group-stream-menu') ||
  customElements.define('xs-group-stream-menu', GroupStreamMenu);
